/* eslint-disable max-len */
import { HttpClient } from '@angular/common/http';
import { ApplicationRef, HostListener, Injectable, NgZone, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
//import { AuthService as Auth0Service } from '@auth0/auth0-angular';
import * as moment from 'moment';
import { BehaviorSubject, Observable, Subscription, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, first, map, skip } from 'rxjs/operators';
import { AppConfigService } from '@Core/services/proprietary/app-config.service';
import { LocalStorage } from '@Core/services/proprietary/browser-storage/local-storage.service';
import { SessionStorage } from '@Core/services/proprietary/browser-storage/session-storage.service';
import { DataService } from '@Core/services/proprietary/data.service';
import { DataSourceStatus, Datasource } from '@Core/services/proprietary/datasource';
import { DatasourceService } from '@Core/services/proprietary/datasource.service';
import { MenuLayoutType, MenuPositionType } from '@Core/services/proprietary/menu.types';
import { PageParameterService } from '@Core/services/proprietary/page-parameter.service';
import { environment } from 'src/environments/environment';
import { cloneDeep } from 'src/ep-utils';
import {
    MinDocDetailWidth,
    MinDocViewerWidth,
    MinPageContentWidth
} from '../../../Views/Work/Accounts/proprietary/controls/document-controls/document-control.types';
import { DesignerElement } from '@Core/models/proprietary/page/page.state';
import { AlertModel } from './alert-dialog/alert-dialog.component';
import { AlertDialogService } from './alert-dialog/alert-dialog.service';
//import { AuthService } from './auth.service';
import { ConfirmModel } from './confirmation-dialog/confirmation-dialog.component';
import { ConfirmationDialogService } from './confirmation-dialog/confirmation-dialog.service';
import { CryptoService } from './crypto.service';
import { ErrorService } from './error.service';
import { MenuService } from './menu.service';
import { ObjectService } from './object.service';
import { PageVisibilityService } from './page-visibility.service';
import { TabService } from './tab.service';

export enum DocPanelLocationType {
    DockedRight,
    NewTab,
    NewWindow
}

export class AppSettingsType {
    Menu: {
        Position: MenuPositionType;
        Layout: MenuLayoutType;
        Width: number;
    };
    ContentPanel: {
        Width: number;
    };
    DocPanel: {
        Location: DocPanelLocationType;
        DefaultLocation: DocPanelLocationType;
        Width: number;
        DetailPanelWidth: number;
        DetailPanelOpen: boolean;
    };
    Debug: {
        ShowAppDataService: boolean;
        ShowPageDataService: boolean;
        ShowRouterService: boolean;
        ShowRuleService: boolean;
    };
    ETM: {
        LocalDevMode: boolean;
        RouteUri: string;
    };
}

@Injectable()
export class AppService implements OnDestroy {
    public AppName: string;

    public SettingsPanelOpen: boolean = false;
    public MainMenuOpen: boolean = true;
    public DocPanelOpen: boolean = false;
    public DesignerMode: boolean = false; // Do not manually set this  -- this is used for peformance only -- use pageOS.setDesignerMode
    public DesignerPropertiesWidth: number = 400;
    public DesignerModeShowHidden: boolean = false;
    private _designerSelectedElements: {
        [fieldPath: string]: DesignerElement;
    } = {};
    private _ContentPanelOpen: boolean = true;
    private _UserAuthenticated: boolean = false;
    public UncaughtException: string = null;
    public PriorUncaughtException: string = null;

    public dsSystemInfo: Datasource;
    private dsFavorite: Datasource;
    private dsRecentItem: Datasource;
    public dsIndicators: Datasource;
    public dsUnreadTasks: Datasource;
    public dsUnfiledDocs: Datasource;
    public dsTaskGet: Datasource; //Used by TaskObjectService    TODO: Is this the right place to put data sources for global services?
    public dsTaskProcess: Datasource; //Used by TaskObjectService    TODO: Is this the right place to put data sources for global services?
    public dsObjectTasks: Datasource; //Used by TaskObjectService    TODO: Is this the right place to put data sources for global services?
    public dsProjectGet: Datasource; //Used by ProjectObjectService    TODO: Is this the right place to put data sources for global services?
    public dsProjectProcess: Datasource; //Used by ProjectObjectService    TODO: Is this the right place to put data sources for global services?
    public dsDocumentGet: Datasource; //Used by DocumentObjectService    TODO: Is this the right place to put data sources for global services?
    public dsDocumentProcess: Datasource; //Used by DocumentObjectService    TODO: Is this the right place to put data sources for global services?
    public dsProductLineConfigGet: Datasource; //Used by ProductLineConfigObjectService    TODO: Is this the right place to put data sources for global services?
    public dsPageLayoutGet: Datasource;
    public dsPageLayoutCollection: Datasource;
    public dsPageLayoutProcess: Datasource;
    public dsUserDefinedTableConfigGet: Datasource;
    public dsComponentSettingGet: Datasource;
    public dsComponentSettingCollection: Datasource;
    private dsOptions: Datasource;

    public pageLevelDataService: DataService;

    public PasswordExpirationDays: number = 999;
    public ETM_Version: string;

    public Username: string = '';
    public UserID: string = '';
    public Systems: any;
    public CurrentSystemCode: string = '';

    /* Expose Application Name, CopyRight, SDK & ETM version requirements for AppInfo.ts */
    public AppInfo = environment;
    public LoginWarning = '';

    public ApplicationName: string = '';
    public ApplicationLogo: string = '';
    public AreaInformation: any = {};

    public TabName: string | null = null;
    public TabCurrentTitle: string | null = null;

    public skipRemoveTabOnUnload = false;
    private reopenMainMenu = false;
    public pageProcessingOverlay = false;
    public loggoutFromAnotherTab = false;

    dsSystemInfoStatusSub: Subscription;
    dsSystemInfoChangeBookStatusSub: Subscription;

    dsObjectConfigStatusSub: Subscription;

    validateTokenSub: Subscription;

    pageVisibilitySub: Subscription;
    networkStatusSub: Subscription;

    dsAuthenticateSub: Subscription;

    //TODO - Move settings to a Settings Service that can be injected into both App and ConceptOneDataService
    //!!!!!! IMPORTANT !!!!! the ETM Settings below are not implemented yet -- See ConceptOne-Datasource service!!!!!!!!!

    // Note: Menu.width is currently fixed at 230.  We do not yet allow for resize
    public Settings: AppSettingsType = {
        Menu: {
            Position: MenuPositionType.Top,
            Layout: MenuLayoutType.TileBig,
            Width: 230
        },
        ContentPanel: {
            Width: null
        },
        DocPanel: {
            Location: DocPanelLocationType.DockedRight,
            DefaultLocation: DocPanelLocationType.DockedRight,
            Width: 500,
            DetailPanelWidth: 200,
            DetailPanelOpen: true
        },
        Debug: {
            ShowAppDataService: false,
            ShowPageDataService: false,
            ShowRouterService: false,
            ShowRuleService: false
        },
        ETM: {
            LocalDevMode: false,
            RouteUri: ''
        }
    };

    // private authState$ = new BehaviorSubject<boolean>(undefined);

    // get authState(): Observable<boolean> {
    //     return this.authState$.asObservable();
    // }

    // setLoggedIn() {
    //     this.authState$.next(true);
    // }

    // setLoggedOut() {
    //     this.authState$.next(false);
    // }

    private SaaSUserBooks: any = [];

    constructor(
        private appRef: ApplicationRef,
        private ds: DataService,
        private dsService: DatasourceService,
        private router: Router,
        private ngZone: NgZone,
        public config: AppConfigService,
        public pageParams: PageParameterService,
        public os: ObjectService,
        private menuService: MenuService,
        private alertService: AlertDialogService,
        private confirmService: ConfirmationDialogService,
        private tabService: TabService,
        //private authService: AuthService,
        private pageVisibility: PageVisibilityService,
        private localStorage: LocalStorage,
        private errorService: ErrorService,
        private sessionStorage: SessionStorage,
        private httpClient: HttpClient,
        //private auth0: Auth0Service,
        private cryptoService: CryptoService
    ) {
        this.dsSystemInfo = ds.addNewDatasource('SystemInfo', 'NO-CACHE');
        this.dsIndicators = ds.addNewDatasource('DBQuery', 'NO-CACHE');
        this.dsFavorite = ds.addNewDatasource('Favorite');
        this.dsRecentItem = ds.addNewDatasource('RecentItem');
        this.dsUnreadTasks = ds.addNewDatasource('Task', 'NO-CACHE');
        this.dsUnfiledDocs = ds.addNewDatasource('Document', 'NO-CACHE');
        this.dsTaskGet = ds.addNewDatasource('Task', 'NO-CACHE');
        this.dsTaskProcess = ds.addNewDatasource('Task', 'NO-CACHE');
        this.dsObjectTasks = ds.addNewDatasource('Task', 'NO-CACHE');
        this.dsProjectGet = ds.addNewDatasource('Project', 'NO-CACHE');
        this.dsProjectProcess = ds.addNewDatasource('Project', 'NO-CACHE');
        this.dsDocumentGet = ds.addNewDatasource('Document', 'NO-CACHE');
        this.dsDocumentProcess = ds.addNewDatasource('Document', 'NO-CACHE');
        this.dsProductLineConfigGet = ds.addNewDatasource('ProductLineConfig', '4H');
        this.dsOptions = ds.addNewDatasource('DBQuery', 'NO-CACHE');
        this.dsPageLayoutGet = ds.addNewDatasource('PageLayoutConfig', '1D');
        this.dsPageLayoutProcess = ds.addNewDatasource('PageLayoutConfig', 'NO-CACHE');
        this.dsPageLayoutCollection = ds.addNewDatasource('PageLayoutConfig', '1D');
        this.dsUserDefinedTableConfigGet = ds.addNewDatasource('UserDefinedTableConfig', '1D');
        this.dsComponentSettingGet = ds.addNewDatasource('ComponentSettingConfig', 'NO-CACHE');
        this.dsComponentSettingCollection = ds.addNewDatasource('ComponentSettingConfig', 'NO-CACHE');

        // // Get App Name from URL
        // this.AppName = window.location.pathname.toString().replace(/\//g, '');

        // this.setAppConfig();
        // this.reloadUserConfig();

        // // Clear encrypted cache as the encyption secret for session storage is now a guid, generated within constuctor of the crypto service or during book change.
        // // If encrypted, cached info in session storage from a previous session will not be decryptable.
        // if (this.sessionStorage.getEncryptionMode()) {
        //     this.dsService.cacheFlushAll();
        //     this.menuService.flushMenuCacheAll();
        //     this.flushCodeSetCache();
        // }

        // if (this.AppInfo.SaaS) {
        //     this.subscribeToAuthenticated();
        // }
    }

    public getAuthApiUrl(): string {
        // let url = environment.AuthApiUrl;
        // if (!url.endsWith('/')) {
        //   url += '/';
        // }

        // return url;
        return '';

    }

    public getAdminApiUrl(): string {
        // let url = environment.AdminApiUrl;
        // if (!url.endsWith('/')) {
        //   url += '/';
        // }

        // return url;
        return '';
    }

    public getApiUrl(): string {
        let url = environment.ApiUrl;
        if (!url.endsWith('/')) {
            url += '/';
        }

        return url;
    }

    public isBillingLicense(): boolean {
        return true; // environment.SaaS == true && environment.License.toLowerCase() === 'scb';
    }

    // private subscribeToAuthenticated() {
    //     if (this.dsAuthenticateSub) {
    //         this.dsAuthenticateSub.unsubscribe();
    //     }
    //     this.dsAuthenticateSub = this.authService.isAuthenticated$.subscribe((authenticated) => {
    //         if (authenticated) {
    //             if (this.dsAuthenticateSub) {
    //                 this.dsAuthenticateSub.unsubscribe();
    //             }
    //             this.saasUserAuthenticated();
    //         }
    //     });
    // }

    public async getUserBooks() {
        return new Promise(async (resolve) => {
            let books = await this.httpClient.get(this.getAuthApiUrl() + 'identities/me/books').toPromise();
            this.SaaSUserBooks = books;
            resolve(books);
        });
    }

    // public async getUserToken() {
    //     return new Promise(async (resolve) => {
    //         this.auth0.idTokenClaims$.pipe(first()).subscribe((token) => {
    //             resolve(token);
    //         });
    //     });
    // }

    public async getAreaInformation(tenantKey: string, areaKey: string) {
        return new Promise(async (resolve) => {
            let area = await this.httpClient.get(this.getAdminApiUrl() + `areas/${tenantKey}/${areaKey}`).toPromise();
            this.AreaInformation = area;
            resolve(area);
        });
    }

    // private async saasUserAuthenticated() {
    //     await this.getUserBooks(); //Get list of Books (book/location combos) that user has rights.
    //     let token: any = await this.getUserToken(); //Get user token.

    //     //Get current book, area, and tenant from token. Used to filter list of books they can switch between.
    //     let currentBook = token['https://insurity.com/claims/book'];
    //     let currentArea = token['https://insurity.com/claims/area'];
    //     let currentTenant = token['https://insurity.com/claims/tenant'];

    //     await this.getAreaInformation(currentTenant, currentArea);
    //     //Filter the list
    //     let books = this.SaaSUserBooks.filter((book) => book.TenantKey == currentTenant && book.AreaKey == currentArea);

    //     //Create same structure of systems the OnPrem model uses so there are fewer code switches between SaaS on OnPrem.
    //     let systems: any = [];
    //     for (var idx in books) {
    //         let book = books[idx];
    //         systems.push({
    //             System_Code: book.TenantKey + ':' + book.AreaKey + ':' + book.BookId, //Build System_Code by combining tenant, area, and bookId values separated by semicolon
    //             Default: book.BookId == currentBook ? 'Y' : 'N', //Set Default based on the book record w/ the same BookId as the value from the token.
    //             Root: 'N', //Not Applicable in SaaS, but still setting to "N"
    //             System_Name: book.Name,
    //             System_Desc: book.Notes
    //         });
    //     }

    //     if (this.dsSystemInfoStatusSub) {
    //         this.dsSystemInfoStatusSub.unsubscribe();
    //     }

    //     this.setSystems(systems);

    //     this.setCurrentBook();
    //     document.cookie;
    //     this.UserAuthenticated = true;
    // }

    loadUserConfig() {
        this.Settings.Menu.Layout = this.config.getUserPref('Menu_Layout');
        this.Settings.Menu.Position = this.config.getUserPref('Menu_Position');
        this.Settings.DocPanel.Width = parseInt(this.config.getUserPref('DocPanel_Width'), 10);
        this.Settings.DocPanel.DetailPanelOpen =
            this.config.getUserPref('DocPanel_DetailPanelOpen') == 'true' ? true : false;
        this.Settings.DocPanel.DetailPanelWidth = parseInt(this.config.getUserPref('DocPanel_DetailPanelWidth'), 10);
    }

    reloadUserConfig() {
        this.config.loadUserPreferences().then(() => {
            this.loadUserConfig();
        });
    }

    saveMenuPreferences() {
        /* TODO:  This is a quick hack to save the menu settings to user preferences.
                 We should probably change the app to read write the values directly from the appconfig service.
            */
        this.config.setUserPref('Menu_Layout', this.Settings.Menu.Layout);
        this.config.setUserPref('Menu_Position', this.Settings.Menu.Position);
        this.config.setUserPref('DocPanel_Width', this.Settings.DocPanel.Width);
        // this.config.setUserPref('DocPanel_DetailPanelOpen', this.Settings.DocPanel.DetailPanelOpen ? 'true' : 'false');
        this.config.setUserPref('DocPanel_DetailPanelWidth', this.Settings.DocPanel.DetailPanelWidth);
    }

    get ContentPanelOpen(): boolean {
        return this._ContentPanelOpen;
    }

    set ContentPanelOpen(value: boolean) {
        if (this._ContentPanelOpen != value) {
            this._ContentPanelOpen = value;
            if (value) {
                if (this.DocPanelOpen) {
                    if (window.innerWidth - MinDocViewerWidth < MinPageContentWidth) {
                        this.DocPanelOpen = false;
                    } else {
                        //default to minimum page content width or docpanel width whichever is narrower
                        let newDocPanelWidth =
                            this.Settings.DocPanel.Width < window.innerWidth - MinPageContentWidth
                                ? this.Settings.DocPanel.Width
                                : window.innerWidth - MinPageContentWidth;

                        //if enough screen width for the last content width then use that width
                        if (this.Settings.ContentPanel.Width && this.Settings.ContentPanel.Width >= MinPageContentWidth) {
                            if (window.innerWidth - MinDocViewerWidth >= this.Settings.ContentPanel.Width) {
                                newDocPanelWidth = window.innerWidth - this.Settings.ContentPanel.Width;
                            }
                        }

                        //determine if the doc detail panel will fit (if it's open)
                        if (this.Settings.DocPanel.DetailPanelOpen) {
                            if (window.innerWidth - MinDocViewerWidth - MinDocDetailWidth < MinPageContentWidth) {
                                this.Settings.DocPanel.DetailPanelOpen = false;
                            } else {
                                if (newDocPanelWidth - this.Settings.DocPanel.DetailPanelWidth < MinDocViewerWidth) {
                                    this.Settings.DocPanel.DetailPanelWidth = MinDocDetailWidth;
                                }
                            }
                        }
                        this.Settings.DocPanel.Width = newDocPanelWidth;
                    }
                }
            } else {
                this.Settings.ContentPanel.Width = window.innerWidth - this.Settings.DocPanel.Width; //save current content width
                this.Settings.DocPanel.Width = window.innerWidth;
            }
        }
    }

    setUncaughtException(value: string) {
        if (!(this.UncaughtException === value)) {
            this.PriorUncaughtException = this.UncaughtException;
            this.UncaughtException = value;
        }
        if (!value) {
            this.PriorUncaughtException = null;
        }
    }

    showUncaughtException() {
        let message = '[Uncaught Exception]\n' + this.UncaughtException;
        if (this.PriorUncaughtException) {
            message = message + '\n\n[Prior Exception]\n' + this.PriorUncaughtException;
        }
        alert(message);
        this.setUncaughtException(null); //clear exception message
    }

    set UserAuthenticated(value: boolean) {
        if (value) {
            this.setCurrentBook();
            this.dsSystemInfo.get({ View: 'Standard' });

            /***** TODO - Temp code to delete all page states from local storage  -- move to pagestate service!!!!  *****/
            this.cacheFlushPageState();

            if (this.dsSystemInfoStatusSub) {
                this.dsSystemInfoStatusSub.unsubscribe();
            }

            this.dsSystemInfoStatusSub = this.dsSystemInfo.status$
                .pipe(filter((status) => status != DataSourceStatus.Loading))
                .subscribe((_) => this.onSystemInfoRetrieveEnd());
        } else {
            this._UserAuthenticated = false;
            this.dsService.cacheFlushAll();
            this.dsIndicators.startRefreshInterval(0); //turn off refresh
            this.dsIndicators.reset();
            this.dsSystemInfo.reset();
            this.dsOptions.reset();
        }
    }

    canLogin(username: string) {
        let userCanLogin = false;
        const currentTabs = this.tabService.getAllTabs();
        if (currentTabs.length == 0) {
            userCanLogin = true;
        } else {
            userCanLogin = currentTabs.every((tab) => tab.User === username);
        }

        return userCanLogin;
    }

    flushAndRetrieveMenus() {
        this.menuService.flushMenuCacheAll();
        this.menuService.getAppMenus();
    }

    async onSystemInfoRetrieveEnd() {
        if (this.dsSystemInfoStatusSub) {
            this.dsSystemInfoStatusSub.unsubscribe();
        }

        const passedSystemVersionsCheck = this.setSystemVersions();
        if (passedSystemVersionsCheck) {
            this.flushAndRetrieveMenus();
            this.os.loadObjects();
            this.dsIndicators.get({
                Procedure_Name: 'epx_user_stats',
                Entity_ID: 'CURRENTUSER',
                Book_Entity_ID: 'CURRENTBOOK',
                Stat_Types: 'Unread_Tasks;Unfiled_Docs'
            });

            this.dsOptions.get({
                Procedure_Name: 'epx_getoptions',
                Entity_ID: 'CURRENTUSER',
                Book_Entity_ID: 'CURRENTBOOK',
                Book_Loc_ID: this.dsSystemInfo.objectNode.Context?.Book_Loc?.Entity_ID
            });

            let userID = this.dsSystemInfo.objectNode.Context.Entity.Entity_ID;
            if (userID !== undefined) {
                this.UserID = this.dsSystemInfo.objectNode.Context.Entity.Entity_ID;
            }

            //Only need to set this for SaaS. OnPrem login flow uses the entered username from the login page.
            if (this.AppInfo.SaaS) {
                this.Username = this.dsSystemInfo?.objectNode?.Context?.Entity?.Entity_Name;
            }
            //this.setLoggedIn();
            this.startPageVisibilityMonitoring();
            //this.startNetworkStatusMonitoring();

            this.dsIndicators.startRefreshInterval(300); //update every 300 seconds (5min)
            this._UserAuthenticated = true;
            return this.os.isObjDictionaryLoaded().then(async (isLoaded) => {
                const routeInfo = this.getInitialRouteAndPageTitle();
                if (!this.TabName) {
                    // Either the user opened a browser and entered the URL or this is a browser page refresh/reload
                    let sysStatusdate = moment(this.getGlobalOptionValue('System_Status_Date'));
                    let today = moment(new Date());
                    if (!sysStatusdate || sysStatusdate.isBefore(today, 'day')) {
                        this.showAlert({
                            title: 'Warning',
                            message:
                                'Contract Status updates were last processed on ' +
                                sysStatusdate.format() +
                                '. It’s likely some or all contract statuses are incorrect. Any changes made to contracts with an incorrect status may cause errors.'
                        });
                    }

                    const sessionTab = this.tabService.getSessionTab();
                    if (sessionTab) {
                        // Before the browser unloads the page, we copy the tab data into session storage which survives the browser page refresh/reload but
                        // not the tab or browser closing.  Since we were able to retrieve this, it means the user reloaded the page so we will try to put
                        // them back to where they were if possible
                        this.TabName = sessionTab.Tab_Name;
                        this.CurrentSystemCode = sessionTab.Book;
                        if (!environment.SaaS) {
                            this.dsService.ETMSystemOverride = sessionTab.Book;
                        }
                        window.name = sessionTab.Tab_Name;
                        this.router.navigate([sessionTab.Start_Route, sessionTab.Start_Route_Params]);
                    } else {
                        // The user opened the browser and entered the URL so create a new tab
                        this.TabName = this.tabService.setTab(
                            routeInfo.route,
                            {},
                            routeInfo.title,
                            this.Username,
                            this.CurrentSystemCode
                        );
                        window.name = this.TabName;
                        this.router.navigate([routeInfo.route]);
                    }
                } else {
                    // Opening a new tab from an existing Sure MGA instance - the TabName gets set in the TokenValidated Route Guard in app-routing module
                    const tab = this.tabService.getTab(this.TabName);
                    this.CurrentSystemCode = tab.Book;
                    if (!environment.SaaS) {
                        this.dsService.ETMSystemOverride = tab.Book;
                    }
                    window.name = this.TabName;
                    this.router.navigate([tab.Start_Route, tab.Start_Route_Params]);
                }
            });
        }
    }

    async onSystemInfoRetrieveEndAfterBookChange() {
        if (this.dsSystemInfoChangeBookStatusSub) {
            this.dsSystemInfoChangeBookStatusSub.unsubscribe();
        }

        const passedSystemVersionsCheck = this.setSystemVersions();
        if (passedSystemVersionsCheck) {
            this.os.refresh();
            this.dsIndicators.refresh();
            this.menuService.getAppMenus();

            this.dsOptions.get({
                Procedure_Name: 'epx_getoptions',
                Entity_ID: 'CURRENTUSER',
                Book_Entity_ID: 'CURRENTBOOK',
                Book_Loc_ID: this.dsSystemInfo.objectNode.Context?.Book_Loc?.Entity_ID
            });

            let userID = this.dsSystemInfo.objectNode.Context.Entity.Entity_ID;
            if (userID !== undefined) {
                this.UserID = this.dsSystemInfo.objectNode.Context.Entity.Entity_ID;
            }

            this.dsIndicators.startRefreshInterval(300); //update every 300 seconds (5min)
            if (this.reopenMainMenu) {
                this.openMainMenu();
            }

            return this.os.isObjDictionaryLoaded().then(async (isLoaded) => {
                const routeInfo = this.getInitialRouteAndPageTitle();
                this.router.navigate([routeInfo.route]); //navigate to root page
                this.pageProcessingOverlay = false; //Turn off so processing overlay also turns off
            });
        } else {
            this.pageProcessingOverlay = false; //Turn off so processing overlay also turns off
            if (this.reopenMainMenu) {
                this.openMainMenu();
            }
        }
    }

    cacheFlushPageState() {
        this.sessionStorage.removeItemOfType('pagestate');
    }

    setMinVersion(appVersion: string): string {
        let versionNumbers = appVersion.split('.');
        versionNumbers[2] = '0';
        versionNumbers[3] = '0';
        let minVersion = versionNumbers.join('.');

        return minVersion;
    }

    setMaxVersion(appVersion: string): string {
        let versionNumbers = appVersion.split('.');
        versionNumbers[2] = '99';
        versionNumbers[3] = '9999';
        let maxVersion = versionNumbers.join('.');

        return maxVersion;
    }

    setSystemVersions() {
        let appVersion = this.AppInfo && '26.4';// this.AppInfo.AppVersion;
        let productVersion = this.dsSystemInfo.objectNode.Product_Version || null;
        let configTimeStamp = this.dsSystemInfo.objectNode.Config_Update_TS || null;

        if (this.dsSystemInfo.status === 'Error' && this.dsSystemInfo.error) {
            let reply = this.dsSystemInfo._replyMessage || '';
            this.LoginWarning = `Configuration Error: ${reply}
      Please contact your Administrator for further assistance.`;
            this.UserAuthenticated = false; //set to false to re-authenticate user
            this.router.navigate(['/login']);
            return false;
        }
        console.info(`Sure MGA Version = ${appVersion}  SDK Version = ${productVersion}`);

        if (!this.AppInfo.SaaS) {
            let sdkMin = this.setMinVersion(appVersion);
            let sdkMax = this.setMaxVersion(appVersion);
            let etmMin = this.setMinVersion(appVersion);
            let etmMax = this.setMaxVersion(appVersion);

            /* Verify SDK Min Version */
            if (this.versionCompare(sdkMin, productVersion) > 0) {
                this.LoginWarning = `The Minimum Sure MGA SDK Version for this Application is ${sdkMin}.
        The Sure MGA SDK Version detected is ${productVersion}`;
                this.UserAuthenticated = false;
                this.router.navigate(['/login']);
                return false;
            }

            /* Verify SDK Max Version */
            if (productVersion !== undefined && this.versionCompare(sdkMax, productVersion) < 0) {
                this.LoginWarning = `The Maximum Sure MGA SDK Version for this Application is ${sdkMax}.
      The Sure MGA SDK Version detected is ${productVersion}`;
                this.UserAuthenticated = false;
                this.router.navigate(['/login']);
                return false;
            }

            /* Verify ETM Min Version */
            if (!this.ETM_Version || this.versionCompare(etmMin, this.ETM_Version) > 0) {
                this.LoginWarning = `The Minimum ETM Version for this Application is ${etmMin}.
      The ETM Version detected is ${this.ETM_Version}`;
                this.UserAuthenticated = false;
                this.router.navigate(['/login']);
                return false;
            }
            /* Verify ETM Max Version */
            if (this.versionCompare(etmMax, this.ETM_Version) < 0) {
                this.LoginWarning = `The Maximum ETM Version for this Application is ${etmMax}.
      The ETM Version detected is ${this.ETM_Version}`;
                this.UserAuthenticated = false;
                this.router.navigate(['/login']);
                return false;
            }
        }

        this.localStorage.setItem('ConfigUpdateTimestamp', configTimeStamp);

        return true;
    }

    getInitialRouteAndPageTitle() {
        let route = null;

        let objectConfig = this.os.getObjectConfig('BookStats');

        if (this.isBillingLicense()) {
            //if this isn't set billing specifically (or happens to be blank) then it would fall through to standard logic of mga
            route = 'billing';
        } else {
            if (!this.dsSystemInfo.objectNode.Context.Team_Roles) {
                // if no team roles exist for user, then either show book-stats page or my-work page
                if (objectConfig && objectConfig.User_Rights && objectConfig.User_Rights.View_Object_Ind == 'Y') {
                    route = 'book-stats';
                } else {
                    // if user does not have access to BookStats object code, display my-work page
                    route = 'my-work';
                }
            } else {
                // otherwise navigate to my-stats page
                route = 'my-stats';
            }
        }

        if (route) {
            let title = this.getRouteTitle(route);
            return {
                route,
                title
            };
        }
    }

    getRouteTitle(routeName: string) {
        if (routeName) {
            if (routeName.indexOf('-') !== -1) {
                let titleStr = routeName.split('-');
                let title1 = titleStr[0].slice(0, 1).toLocaleUpperCase() + titleStr[0].slice(1);
                let title2 = titleStr[1].slice(0, 1).toLocaleUpperCase() + titleStr[1].slice(1);
                return `${title1} ${title2}`;
            } else {
                return routeName.slice(0, 1).toLocaleUpperCase() + routeName.slice(1);
            }
        }

        return '';
    }

    setAppConfig() {
        //set global settings in AppConfigService
        this.config.allowClientDebug = !this.AppInfo.production; // Debug tool only enabled in non-production.
        this.errorService.setIsClientDebugMode(this.config.allowClientDebug);

        // Local and Session storage is encrypted for production environments.
        this.sessionStorage.setEncryptionMode(true); //Change to false for local development if you want to be able to inspect session storage unencrypted
        this.localStorage.setEncryptionMode(true); //Change to false for local development if you want to be able to inspect local storage unencrypted
        this.ApplicationName = 'Insurity Pro Suite'; //environment.ApplicationName || 'Sure MGA';
        this.ApplicationLogo = 'assets/images/logo-sure-mga-c1.svg';
        this.checkActiveTabs();
    }

    toggleDebugAccess(enabled: boolean) {
        this.config.allowClientDebug = enabled;
        this.errorService.setIsClientDebugMode(enabled);
    }

    checkActiveTabs() {
        if (!this.TabName && !this.UserAuthenticated) {
            const activeTabsExist = this.tabService.activeTabsExist();
            // if (!environment.SaaS && activeTabsExist) {
            //     // Only validate authtoken for OnPrem. Auth0 manages token validation for SaaS.
            //     this.validateAuthToken();
            // }
        }
    }

    // async validateAuthToken() {
    //     if (this.validateTokenSub) {
    //         this.validateTokenSub.unsubscribe();
    //     }
    //     //Double check not in SaaS mode before calling the OnPrem JWT token validation.
    //     if (!environment.SaaS) {
    //         this.validateTokenSub = this.authService
    //             .validateOnPremToken()
    //             .pipe(
    //                 map((response) => this.handleValidateOnPremTokenSuccess(response)),
    //                 catchError((error) => of(this.handleValidateOnPremTokenFailure(error)))
    //             )
    //             .subscribe();
    //     }
    // }

    // private handleValidateOnPremTokenFailure(error: any) {
    //     this.LoginWarning = `Auth Token Validation Failed`;
    //     this.router.navigate(['/login']).then(() => {
    //         this.tabService.cleanupAllTabs();
    //         this.TabName = null;
    //         window.location.reload();
    //     });
    // }

    // private handleValidateOnPremTokenSuccess(response: any) {
    //     const activeTab = this.tabService.getActiveTabBookAndUser();
    //     this.Username = response.Username;
    //     this.ETM_Version = response.ETMReply.ETM_Version;
    //     this.PasswordExpirationDays = response.ETMReply.Authenticate.User.Status.Password_Expiration_Date.Days * 1;
    //     if (this.setSystems(response.ETMReply.Authenticate.User.Systems.System)) {
    //         this.setCurrentBook(activeTab);

    //         if (
    //             response.ETMReply.Authenticate.User.Status.Password_Status == 'Temporary' ||
    //             response.ETMReply.Authenticate.User.Status.Password_Status == 'Expired'
    //         ) {
    //             this.UserAuthenticated = false; //force password change
    //             this.router.navigate([
    //                 '/account',
    //                 { Mode: 'PasswordChange', Password_Status: response.Authenticate.User.Status.Password_Status }
    //             ]);
    //             return;
    //         }

    //         this.UserAuthenticated = true;
    //     } else {
    //         this.UserAuthenticated = false;
    //     }
    // }

    setSystems(retrievedSystems: any = []): boolean {
        let systems = cloneDeep(retrievedSystems);

        for (let idx = systems.length - 1; idx >= 0; idx--) {
            if (systems[idx].Root == 'Y') {
                systems.splice(idx, 1);
            }
        }

        let defaultIdx = systems.map((sys) => sys.Default).indexOf('Y');
        if (defaultIdx == -1 && systems[0]) {
            systems[0].Default = 'Y';
        }
        if (systems.length > 0) {
            this.Systems = systems;
            return true;
        } else {
            this.showAlert({
                title: 'Error',
                message: 'No Configured Systems found for your account. Contact your system administrator.'
            });

            return false;
        }
    }

    setCurrentBook(activeTab?: any) {
        if (this.CurrentSystemCode == '') {
            let book;
            if (activeTab) {
                book = activeTab.Book;
            } else if (this.TabName) {
                // Tab opened from existing Sure MGA instance
                const tab = this.tabService.getTab(this.TabName);
                if (tab && tab.Book) {
                    book = tab.Book;
                }
            }

            if (!book && this.Systems) {
                book = this.Systems.find((system) => system.Default === 'Y').System_Code;
            }

            if (book) {
                this.CurrentSystemCode = book;
                if (!environment.SaaS) {
                    this.dsService.ETMSystemOverride = book;
                }
            }
        }
    }

    async onUserLogOut() {
        this.Settings.DocPanel.DetailPanelOpen = false;
        this.ContentPanelOpen = true;
        this.DocPanelOpen = false;

        this.stopDatasourcePolling();
        this.stopPageVisibilityMonitoring();
        //this.stopNetworkStatusMonitoring();

        // this.setLoggedOut();
        this.UserAuthenticated = false;
        this.DesignerMode = false;

        if (this.TabName) {
            this.localStorage.setItem('tab:logged_out', this.TabName);
            this.cleanupTabOnLogout();
            this.tabService.cleanupTab(this.TabName);
            this.TabName = null;
        }

        if (environment.SaaS) {
            this.router.navigate(['/logout']);
        } else {
            //this.authService.onPremLogout();
            this.router.navigate(['/login']);
        }
    }

    logOutDetectedOnOtherTab() {
        if (this._UserAuthenticated) {
            this.stopDatasourcePolling();
            this.stopPageVisibilityMonitoring();
            //this.stopNetworkStatusMonitoring();
            //this.setLoggedOut();
            this.UserAuthenticated = false;
        }

        if (this.TabName) {
            this.tabService.cleanupTab(this.TabName);
            this.TabName = null;
        }

        if (environment.SaaS) {
            this.loggoutFromAnotherTab = true;
            this.router.navigate(['/logout']);
            window.self.close(); // Close the tabs opened from within Sure MGA when detecting logout from another.
        } else {
            this.router.navigate(['/login']);
        }
    }

    logoutAfterOfflineDetected() {
        this.logOutDetectedOnOtherTab();
    }

    cleanupTabOnLogout() {
        const sessionTimeoutPaused = this.localStorage.getItem('tab:session_timeout_paused');
        if (sessionTimeoutPaused && sessionTimeoutPaused == this.TabName) {
            this.localStorage.removeItem('tab:session_timeout_paused');
        }

        const warningDialogOpened = this.localStorage.getItem('tab:warning_dialog_opened');
        if (warningDialogOpened && warningDialogOpened == this.TabName) {
            this.localStorage.removeItem('tab:warning_dialog_opened');
        }

        this.alertService.closeTimeoutWarning();
    }

    stopDatasourcePolling() {
        this.ds.stopAllRefreshIntervals();
    }

    pauseAllDatasourcePolling() {
        this.ds.pauseAllRefreshIntervals();
    }

    resumeAllDatasourcePolling() {
        this.ds.resumeAllRefreshIntervals();
    }

    startPageVisibilityMonitoring() {
        if (this.pageVisibilitySub) {
            this.pageVisibilitySub.unsubscribe();
        }

        this.pageVisibilitySub = this.pageVisibility
            .visibleChanges()
            .pipe(skip(1))
            .subscribe((pageVisible) => {
                if (pageVisible) {
                    this.ds.resumeAllRefreshIntervals();
                } else {
                    this.ds.pauseAllRefreshIntervals();
                }
            });
    }

    stopPageVisibilityMonitoring() {
        if (this.pageVisibilitySub) {
            this.pageVisibilitySub.unsubscribe();
        }
    }

    // startNetworkStatusMonitoring() {
    //     this.onlineStatus.startMonitoring();
    //     if (this.networkStatusSub) {
    //         this.networkStatusSub.unsubscribe();
    //     }

    //     this.networkStatusSub = this.onlineStatus
    //         .networkChanges()
    //         .pipe(
    //             filter((status) => !status),
    //             distinctUntilChanged()
    //         )
    //         .subscribe((status) => {
    //             if (!status) {
    //                 this.onConnectionLost();
    //             }
    //         });
    // }

    // stopNetworkStatusMonitoring() {
    //     if (this.networkStatusSub) {
    //         this.networkStatusSub.unsubscribe();
    //     }
    //     this.onlineStatus.stopMonitoring();
    // }

    // onConnectionLost() {
    //     if (this.networkStatusSub) {
    //         this.networkStatusSub.unsubscribe();
    //     }
    //     this.onlineStatus.stopMonitoring();
    //     this.stopDatasourcePolling();
    //     this.stopPageVisibilityMonitoring();
    //     this.onlineStatus.setOffline();
    // }

    public onPageTitleChange(title: string) {
        this.TabCurrentTitle = title;
        if (this.TabName) {
            this.tabService.setTabPageTitle(title, this.TabName);
        }
    }

    public getCurrentPageTitle() {
        return this.TabCurrentTitle;
    }

    public onTabRouteChange(newRoute: string) {
        if (this.TabName) {
            let url = this.router.url;
            if (url.startsWith('/account')) {
                return;
            }
            let newUrl = newRoute;
            let pageID: string = '';
            let pageParams: any = {};

            if (url.indexOf('PageID') > -1) {
                pageID = url.split('PageID=')[1];
                if (pageID) {
                    pageParams = this.pageParams.getParameterSet(pageID);
                }
                if (url.indexOf(';') > -1) {
                    url = url.split(';')[0];
                }
            } else {
                if (url.indexOf(';') > -1) {
                    let urlFragments = url.split(';');
                    url = urlFragments[0];
                    let paramFragment = urlFragments[1];
                    if (paramFragment.indexOf('=') > -1) {
                        let splitParams = paramFragment.split('=');
                        let key = splitParams[0];
                        let value = splitParams[1];
                        pageParams[key] = value;
                    }
                }
            }

            let newStartRoute = url;
            this.tabService.updateTabStartRoute(this.TabName, newStartRoute, pageParams);
        }
    }

    get UserAuthenticated(): boolean {
        return this._UserAuthenticated;
    }

    get currentSystemCode() {
        let systemName = '';
        if (!this.CurrentSystemCode) {
            systemName = this.Systems.find((system) => system.Default === 'Y').System_Name;
        } else {
            systemName = this.Systems.find((system) => system.System_Code === this.CurrentSystemCode).System_Name;
        }

        return systemName;
    }

    showTimeoutWarningAlert() {
        if (this.UserAuthenticated) {
            this.alertService.showTimeoutWarning();
        }
    }

    // async startBookChange(systemCode: string) {
    //     if (systemCode !== this.CurrentSystemCode) {
    //         if (this.MainMenuOpen) {
    //             this.closeMainMenu();
    //         }
    //         this.pageProcessingOverlay = true;
    //         if (environment.SaaS) {
    //             // SaaS pre-steps for book change

    //             //Check for multi tabs.
    //             let allTabs = this.tabService.getAllTabs();
    //             if (allTabs.length > 1) {
    //                 this.alertService.showAlert(
    //                     'Warning',
    //                     'All other Sure MGA tabs must be closed before you can change to a different Book or Location.',
    //                     'OK',
    //                     'lg'
    //                 );
    //                 this.pageProcessingOverlay = false;
    //                 if (this.reopenMainMenu) {
    //                     this.openMainMenu();
    //                 }
    //                 return;
    //             }

    //             // SystemCode built using <Tentant>:<Area>:<BookId>
    //             let newBookId = systemCode.split(':').pop(); //Split to an array using : as delimiter and grab the last piece as the newBookId.
    //             let newBook: any = {};

    //             for (var idx in this.SaaSUserBooks) {
    //                 if (this.SaaSUserBooks[idx].BookId == newBookId) {
    //                     newBook = this.SaaSUserBooks[idx];
    //                     break;
    //                 }
    //             }

    //             // Should have always found the record, but adding this check just in case.
    //             if (newBook) {
    //                 // Update selectedRealm with the new book.
    //                 await this.httpClient
    //                     .put(this.getAuthApiUrl() + 'identities/me/SelectedRealm', {
    //                         tenantKey: newBook.TenantKey,
    //                         license: newBook.License,
    //                         areaKey: newBook.AreaKey,
    //                         bookId: newBook.BookId
    //                     })
    //                     .toPromise();

    //                 // Request new token so claims info is updated from the new selectedRealm.
    //                 this.auth0.getAccessTokenSilently({ cacheMode: 'off', detailedResponse: true }).subscribe(
    //                     (res) => {
    //                         // Once new token successfully obtained, call "base" change book logic.
    //                         this.changeBook(systemCode);
    //                     },
    //                     (err) => {
    //                         console.log(err);
    //                         this.pageProcessingOverlay = false; //Turn off so processing overlay also turns off
    //                         if (this.reopenMainMenu) {
    //                             this.openMainMenu();
    //                         }
    //                     }
    //                 );
    //             } else {
    //                 this.pageProcessingOverlay = false; //Turn off so processing overlay also turns off
    //                 if (this.reopenMainMenu) {
    //                     this.openMainMenu();
    //                 }
    //             }
    //         } else {
    //             // "Base" change book logic is immediately called for OnPrem.
    //             this.changeBook(systemCode);
    //         }
    //     }
    // }

    changeBook(systemCode: string) {
        if (this.dsSystemInfoChangeBookStatusSub) {
            this.dsSystemInfoChangeBookStatusSub.unsubscribe();
        }
        this.cryptoService.generateSessionSecret();
        this.dsService.ETMSystemOverride = systemCode;
        this.CurrentSystemCode = systemCode;
        this.tabService.updateTabOnBookChange(this.TabName, systemCode);
        this.dsService.cacheFlushAll();
        this.dsSystemInfo.refresh();
        this.menuService.flushMenuCacheAll();
        this.flushCodeSetCache();
        this.dsSystemInfoChangeBookStatusSub = this.dsSystemInfo.status$
            .pipe(filter((status) => status != DataSourceStatus.Loading))
            .subscribe((_) => this.onSystemInfoRetrieveEndAfterBookChange());
    }

    setFavorite(objectCode: string, objectID: string, on: boolean) {
        //TODO: Refine how "process" method of dataservice works?    Should it handle the ConceptOneRequest and Transaction Layers???
        //TODO: SHOULD NOT BE REQUIRED TO SEND IN SDK VERSION.    THIS TO GET AROUND A JSON CONVERTER BUG

        let body = {
            ConceptOneRequest: {
                SDK_Version: this.dsSystemInfo.data.ConceptOneReply.SDK_Version,
                FavoriteProcess: {
                    Favorite: {
                        '@Object_Code': objectCode,
                        '@Object_ID': objectID,
                        '@Edit_Status': on ? 'A' : 'D'
                    }
                }
            }
        };
        //console.log(body)
        this.dsService.process(this.dsFavorite, {}, body);
    }

    setRecentItem(objectCode: string, objectID: string, on: boolean = true) {
        //TODO: Refine how "process" method of dataservice works?    Should it handle the ConceptOneRequest and Transaction Layers???
        let body = {
            ConceptOneRequest: {
                SDK_Version: this.dsSystemInfo.data.ConceptOneReply.SDK_Version,
                RecentItemProcess: {
                    RecentItem: {
                        '@Object_Code': objectCode,
                        '@Object_ID': objectID,
                        '@Edit_Status': on ? 'A' : 'D'
                    }
                }
            }
        };
        this.dsService.process(this.dsRecentItem, {}, body);
    }

    clearAppLevelDataErrors() {
        this.ds.clearErrors();
    }

    showConfirm(options: ConfirmModel, confirmed: boolean) {
        if (!options) {
            options = { title: 'Confirm', message: 'No message provided' };
        }

        let confirmPromise = this.confirmService.confirm(options.title, options.message);
        confirmPromise.then(function (result) {
            confirmed = result;
        });
    }

    showAlert(content: AlertModel, options?: {}) {
        if (!content) {
            content = { title: '', message: 'No message provided' };
        }

        this.alertService.showAlert(content.title, content.message);
    }

    //TODO - Find a better solution for stopNavigation?
    // This is used to prevent a navigation click from propagating to a parent control
    // Usage in HTML template:   (click)="task.showDetail=!task.showDetail;app.stopNavigation($event)"
    stopNavigation(e: Event) {
        //prevent click event from propogating to ancestors
        //e.stopImmediatePropagation()
        e.stopPropagation(); // modern browsers (IE9 and above)
        e.cancelBubble = true;
    }

    versionCompare(v1: string, v2: string) {
        try {
            var v1parts = v1.split('.');
            var v2parts = v2.split('.');
        } catch (err) {
            return -1;
        }

        //insert zeros to match part length
        while (v1parts.length < v2parts.length) {
            v1parts.push('0');
        }
        while (v2parts.length < v1parts.length) {
            v2parts.push('0');
        }

        for (var i = 0; i < v1parts.length; ++i) {
            if (v2parts.length == i) {
                return 1;
            }

            if (v1parts[i] == v2parts[i]) {
                continue;
            } else if (v1parts[i] > v2parts[i]) {
                return 1;
            } else {
                return -1;
            }
        }

        return 0;
    }

    //TODO: move this to some other service?
    getCodedDate(sDateCode: string, sMode: string = 'BEGIN', format: string = null) {
        // gets date from a code
        //  TODAY, TODAY-1Y, ANY, ANYTIME, L7D, L30D, L30D-1Y L90D, L90D-1Y, L12M, L12M-1Y, YTD, YTD+1, YTD-1, Q, Q+1, Q-3,  M, M+1, M-2,

        if (sMode.toUpperCase() != 'BEGIN' && sMode.toUpperCase() != 'END' && sMode.toUpperCase() != 'DESC') {
            return '[Invalid Mode parameter]';
        }

        var calcDate = new Date();
        var mode = sMode.toUpperCase();
        var dateCode = sDateCode.toUpperCase();
        var desc = '?';

        //TODAY
        if (dateCode == 'TODAY') {
            desc = 'Today';
        }

        //TODAY LAST YEAR
        if (dateCode == 'TODAY-1Y') {
            desc = 'Same Day Prior Year';
            var yr = calcDate.getFullYear() - 1;
            var mn = calcDate.getMonth();
            var dy = calcDate.getDate();
            calcDate.setFullYear(yr, mn, dy);
        } else if (dateCode == 'TODAY+1Y') {
            desc = 'Same Day Next Year';
            var yr = calcDate.getFullYear() + 1;
            var mn = calcDate.getMonth();
            var dy = calcDate.getDate();
            calcDate.setFullYear(yr, mn, dy);
        } else if (dateCode == 'ANY' || dateCode == 'ANYTIME') {
            //Anytime
            desc = 'Anytime';
            if (mode == 'BEGIN') {
                calcDate.setFullYear(1901, 0, 1);
            } else {
                calcDate.setFullYear(9999, 11, 31);
            }
        } else if (dateCode == 'L7D') {
            //Last 7 Days
            desc = 'Last 7 Days';
            if (mode == 'BEGIN') {
                calcDate.setDate(calcDate.getDate() - 7);
            } else {
                calcDate = calcDate;
            }
        } else if (dateCode == 'L30D') {
            //Last 30 Days
            desc = 'Last 30 Days';
            if (mode == 'BEGIN') {
                calcDate.setDate(calcDate.getDate() - 30);
            } else {
                calcDate = calcDate;
            }
        } else if (dateCode == 'L30D-1Y') {
            //Last 30 Days Prior Year
            desc = 'Same 30 Days Prior Year';
            if (mode == 'BEGIN') {
                calcDate.setDate(calcDate.getDate() - 30);
            } else {
                calcDate = calcDate;
            }

            var yr = calcDate.getFullYear() - 1;
            var mn = calcDate.getMonth();
            var dy = calcDate.getDate();
            calcDate.setFullYear(yr, mn, dy);
        } else if (dateCode == 'L90D') {
            //Last 90 Days
            desc = 'Last 90 Days';
            if (mode == 'BEGIN') {
                calcDate.setDate(calcDate.getDate() - 90);
            } else {
                calcDate = calcDate;
            }
        } else if (dateCode == 'L90D-1Y') {
            //Last 90 Days Prior Year
            desc = 'Same 90 Days Prior Year';
            if (mode == 'BEGIN') {
                calcDate.setDate(calcDate.getDate() - 90);
            } else {
                calcDate = calcDate;
            }
            var yr = calcDate.getFullYear() - 1;
            var mn = calcDate.getMonth();
            var dy = calcDate.getDate();
            calcDate.setFullYear(yr, mn, dy);
        } else if (dateCode == 'L12M') {
            //Last 12 Months
            desc = 'Last 12 Months';
            if (mode == 'BEGIN') {
                calcDate.setMonth(calcDate.getMonth() - 12);
            } else {
                calcDate = calcDate;
            }
        } else if (dateCode.slice(0, 3) == 'YTD') {
            //Year to Date, YTD,  YTD-1
            var yr = calcDate.getFullYear();
            var relative = +dateCode.slice(3, 8);
            if (mode == 'BEGIN' || mode == 'DESC') {
                yr = yr + relative;
                calcDate.setFullYear(yr, 0, 1);

                desc = 'YTD ' + calcDate.getFullYear();
            } else {
                yr = yr + relative;
                calcDate.setFullYear(yr);
            }
        } else if (dateCode.slice(0, 1) == 'Q') {
            //QUARTERS Q, Q+3, Q-2
            var yr = calcDate.getFullYear();
            var mn = calcDate.getMonth();
            var q = 1;
            var relative = +dateCode.slice(1, 5);
            if (mode == 'BEGIN' || mode == 'DESC') {
                if (mn < 3) {
                    mn = 0;
                } else if (mn < 6) {
                    mn = 3;
                } else if (mn < 9) {
                    mn = 6;
                } else {
                    mn = 9;
                }
                mn = mn + relative * 3;
                calcDate.setFullYear(calcDate.getFullYear(), mn, 1);

                mn = calcDate.getMonth();
                if (mn < 3) {
                    q = 1;
                } else if (mn < 6) {
                    q = 2;
                } else if (mn < 9) {
                    q = 3;
                } else {
                    q = 4;
                }
                desc = 'Q' + q + '-' + calcDate.getFullYear();
            } else {
                if (mn < 3) {
                    mn = 3;
                } else if (mn < 6) {
                    mn = 6;
                } else if (mn < 9) {
                    mn = 9;
                } else {
                    mn = 12;
                }
                mn = mn + relative * 3;
                calcDate.setFullYear(calcDate.getFullYear(), mn, 0);
            }
        } else if (dateCode.slice(0, 1) == 'M') {
            //Months M, M+3, M-2
            var monthName = new Array(13);
            monthName[0] = 'Jan';
            monthName[1] = 'Feb';
            monthName[2] = 'Mar';
            monthName[3] = 'Apr';
            monthName[4] = 'May';
            monthName[5] = 'Jun';
            monthName[6] = 'Jul';
            monthName[7] = 'Aug';
            monthName[8] = 'Sep';
            monthName[9] = 'Oct';
            monthName[10] = 'Nov';
            monthName[11] = 'Dec';

            var yr = calcDate.getFullYear();
            var mn = calcDate.getMonth();
            var relative = +dateCode.slice(1, 5);
            if (mode == 'BEGIN' || mode == 'DESC') {
                mn = mn + relative;
                calcDate.setFullYear(calcDate.getFullYear(), mn, 1);

                desc = monthName[mn] + ' ' + calcDate.getFullYear();
            } else {
                calcDate.setFullYear(calcDate.getFullYear(), mn, 0);
            }
        }

        if (mode == 'DESC') {
            return desc;
        } else {
            //format date
            var dd: any = calcDate.getDate();
            var mm: any = calcDate.getMonth() + 1; //january is 0 in javascript
            var yyyy = calcDate.getFullYear();
            var yy = yyyy.toString().slice(2, 4);

            if (dd < 10) {
                dd = '0' + dd;
            }

            if (mm < 10) {
                mm = '0' + mm;
            }

            if (format) {
                switch (format.toLowerCase()) {
                    case 'yyyy-mm-dd':
                        return yyyy + '-' + mm + '-' + dd;
                    case 'mm-dd-yyyy':
                        return mm + '-' + dd + '-' + yyyy;
                    case 'mm-dd-yy':
                        return mm + '-' + dd + '-' + yy;
                    default:
                        return '[Invalid date format: ' + format.toLowerCase();
                }
            }

            return yyyy + '-' + mm + '-' + dd;
        }
    }

    @HostListener('window:beforeunload')
    ngOnDestroy() {
        if (this.dsSystemInfoStatusSub) {
            this.dsSystemInfoStatusSub.unsubscribe();
        }

        if (this.dsSystemInfoChangeBookStatusSub) {
            this.dsSystemInfoChangeBookStatusSub.unsubscribe();
        }

        if (this.validateTokenSub) {
            this.validateTokenSub.unsubscribe();
        }

        if (this.dsObjectConfigStatusSub) {
            this.dsObjectConfigStatusSub.unsubscribe();
        }

        if (this.pageVisibilitySub) {
            this.pageVisibilitySub.unsubscribe();
        }

        if (this.networkStatusSub) {
            this.networkStatusSub.unsubscribe();
        }

        if (this.dsAuthenticateSub) {
            this.dsAuthenticateSub.unsubscribe();
        }

        const sessionTimeoutPaused = this.localStorage.getItem('tab:session_timeout_paused');
        if (sessionTimeoutPaused && sessionTimeoutPaused == this.TabName) {
            this.localStorage.removeItem('tab:session_timeout_paused');
        }

        const warningDialogOpened = this.localStorage.getItem('tab:warning_dialog_opened');
        if (warningDialogOpened && warningDialogOpened == this.TabName) {
            this.localStorage.removeItem('tab:warning_dialog_opened');
        }

        const isLastTab = this.TabName ? this.tabService.isLastTab(this.TabName) : false;
        if (isLastTab) {
            const tabLoggedOut = this.localStorage.getItem('tab:logged_out');
            if (tabLoggedOut && tabLoggedOut == this.TabName) {
                this.localStorage.removeItem('tab:logged_out');
            }
        }

        if (this.TabName) {
            this.tabService.removeTab(this.TabName);
            this.TabName = null;
        }
    }

    updateLayout() {
        // console.log('App.updateLayout invoked')
        window.dispatchEvent(new Event('resize'));
        // //app layout changed.

        // // HACK used to force a resize event.   Used to make dynamic size controls resize.  Look for bindings to (window:resize)
        // setTimeout(() => {
        //   window.dispatchEvent(new Event('resize'));
        // }, 100);
    }

    getGlobalOptionValue(optionName: string) {
        return this.getOptionValue('Global', optionName);
    }

    getBookOptionValue(optionName: string) {
        return this.getOptionValue('Book', optionName);
    }

    getUserOptionValue(optionName: string) {
        return this.getOptionValue('User', optionName);
    }

    getRoleOptionValue(optionName: string) {
        return this.getOptionValue('Role', optionName);
    }

    getSecurityOptionValue(optionName: string) {
        return this.getOptionValue('Security', optionName);
    }

    hasControlledDataRights(tableName: string, columnName: string): 'Y' | 'N' {
        let data: any = [];

        if (this.getGlobalOptionValue('Enable_SDK_PII_Protection_Ind') != 'Y') {
            return 'Y'; // Return Y if global options for SDK PII Protection isn't turned on.
        } else {
            if (
                this.dsOptions &&
                this.dsOptions.objectNode &&
                this.dsOptions.objectNode.Options &&
                this.dsOptions.objectNode.Options.Controlled_Data &&
                this.dsOptions.objectNode.Options.Controlled_Data.Data
            ) {
                if (this.dsOptions.objectNode.Options.Controlled_Data.Data instanceof Array) {
                    data = cloneDeep(this.dsOptions.objectNode.Options.Controlled_Data.Data);
                } else {
                    data.push(this.dsOptions.objectNode.Options.Controlled_Data.Data);
                }

                data = data.filter((idx) => idx.Controlled_Table.toLowerCase() == tableName.toLowerCase());
                for (var i in data) {
                    if (data[i].Controlled_Column.toLowerCase() == columnName.toLowerCase()) {
                        return 'Y';
                    }
                }
            }
        }

        return 'N';
    }

    getLocationOptionValue(bookLocID: any, optionName: string) {
        let result: string = null;
        if (
            this.dsOptions &&
            this.dsOptions.objectNode &&
            this.dsOptions.objectNode.Options &&
            this.dsOptions.objectNode.Options.Location
        ) {
            if (this.dsOptions.objectNode.Options.Location instanceof Array) {
                for (var i in this.dsOptions.objectNode.Options.Location) {
                    if (this.dsOptions.objectNode.Options.Location[i].Entity_ID == bookLocID) {
                        if (this.dsOptions.objectNode.Options.Location[i][optionName]) {
                            result = this.dsOptions.objectNode.Options.Location[i][optionName];
                        }
                        break;
                    }
                }
            } else {
                if (this.dsOptions.objectNode.Options.Location[optionName]) {
                    result = this.dsOptions.objectNode.Options.Location[optionName];
                }
            }
        }

        return result;
    }

    base64Decode(data: string) {
        const text = atob(data);
        const length = text.length;
        const bytes = new Uint8Array(length);
        for (let i = 0; i < length; i++) {
            bytes[i] = text.charCodeAt(i);
        }
        const decoder = new TextDecoder();

        return decoder.decode(bytes);
    }

    base64Encode(data: string) {
        const length = data.length;
        const bytes = new Uint8Array(length);
        for (let i = 0; i < length; i++) {
            bytes[i] = data.charCodeAt(i);
        }

        let dataBytes = String.fromCodePoint(...bytes);

        dataBytes = dataBytes.replace('&rsquo;', '&#39;');
        dataBytes = dataBytes.replace(/\u0019/g, '&#39;');
        dataBytes = dataBytes.replace(/\u0013/g, '&#8209;');

        return btoa(dataBytes);
    }

    //Private to appService. Use one of the optionType specific methods when needing an option value.
    private getOptionValue(type: string, name: string) {
        if (!type || !name) {
            return null;
        }

        if (
            this.dsOptions &&
            this.dsOptions.objectNode &&
            this.dsOptions.objectNode.Options &&
            this.dsOptions.objectNode.Options[type] &&
            this.dsOptions.objectNode.Options[type][name]
        ) {
            return this.dsOptions.objectNode.Options[type][name];
        } else {
            return null;
        }
    }

    flushCodeSetCache() {
        this.sessionStorage.removeItemOfType('codeset');
    }

    closeMainMenu() {
        this.reopenMainMenu = true;
        this.MainMenuOpen = false;
        this.updateLayout();
    }

    openMainMenu() {
        this.reopenMainMenu = false;
        this.MainMenuOpen = true;
        this.updateLayout();
    }

    getSelectedText() {
        var selection = window.getSelection();
        return !(selection && selection.type === 'Range');
    }

    // UX Designer Global Properties -- Consider later refactor to move to separate service
    designerSelectElement(elementName: string, designerElement: DesignerElement, selected: boolean = true) {
        if (selected && !this._designerSelectedElements[elementName]) {
            this._designerSelectedElements[elementName] = designerElement;
        }
        if (!selected && this._designerSelectedElements[elementName]) {
            delete this._designerSelectedElements[elementName];
        }
    }

    designerClearElementSelection() {
        this._designerSelectedElements = {};
    }

    designerElementIsSelected(elementName: string): boolean {
        return this._designerSelectedElements[elementName] != null;
    }

    public get designerSelectedElements(): { [fieldPath: string]: DesignerElement } {
        return this._designerSelectedElements;
    }

    public get designerSelectedElementCount(): number {
        return Object.keys(this._designerSelectedElements).length;
    }
}
